import { Box } from "@mui/material";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import CloudUploadSVG from "../../../../../../assets/images/icons/CloudUpload.tsx";
import BriaColorPicker from "../../../../../../components/common/BriaColorPicker/BriaColorPicker";
import BriaInput from "../../../../../../components/common/BriaInput/BriaInput";
import BriaSlider from "../../../../../../components/common/BriaSlider/BriaSlider";
import ImageUploader from "../../../../../../components/common/ImageUploader/ImageUploader.tsx";
import StoppableButton from "../../../../../../components/common/StoppableButton/StoppableButton.tsx";
import {
	DEFAULT_BACKGROUND_CONFIGS,
	GENERATE_BY_TEXT_CONFIGS,
} from "../../../../../../constants/ImageToImageConstants.ts";
import LinkConstants from "../../../../../../constants/LinkConstants.ts";
import { useImageToImageConfig } from "../../../../../../hooks/useImageToImageConfig";
import { useLogEvents } from "../../../../../../hooks/useLogEvents.ts";
import { useManualPlacementConfig } from "../../../../../../hooks/useManualPlacementConfig.ts";
import { useAppStore } from "../../../../../../hooks/useStores";
import InputLayout from "../../../../../../layout/InputLayout/InputLayout";
import {
	ImageEditingViewMode,
	ImageToImageConfigType,
	type ConfigValue,
} from "../../../../../../models/image-to-image";
import type { SyntheticEventProps } from "../../../../../../models/text-to-image.ts";
import { isAutomotiveApp } from "../../../../../../utils/index.ts";
import useImageUtils from "../../../../../../utils/useImageUtils.tsx";
import Config from "../../Config/Config";
import styles from "./BackgroundConfig.module.scss";
import GenerateBackgroundModesCard from "./GenerateBackgroundModesCard/GenerateBackgroundModesCard.tsx";

export type SubMenuConfigProps = {
	onSubMenuOpen: (open: boolean) => void;
	hiddenConfigs?: string[];
};

type OperationType = "generateByText" | "generateByImage" | "solid" | "remove" | "blur";

interface OperationState {
	isLoading: boolean;
	error: string | null;
	lastOperation: OperationType | null;
}

const BackgroundConfig = ({ onSubMenuOpen, hiddenConfigs }: SubMenuConfigProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.imageToImage.config.features.background" });

	const { imageToImageStore, playgroundStore } = useAppStore();
	const { getBase64 } = useImageUtils();
	const {
		updateSelectedBackgroundConfigs,
		canApplySolidBackground,
		canGenerateBackgroundByText,
		canGenerateBackgroundByImage,
		countSelected,
	} = useImageToImageConfig();
	const { checkResolutions } = useManualPlacementConfig();
	const {
		logGenerateByTextEvent,
		logGenerateByImageEvent,
		logSolidBackgroundEvent,
		logRemoveBackgroundEvent,
		logBlurBackgroundEvent,
		logAutomotiveGenerateByTextEvent,
		logAutomotiveGenerateByImageEvent,
		logAutomotiveSolidBackgroundEvent,
		logAutomotiveRemoveBackgroundEvent,
	} = useLogEvents();

	const solidColor = imageToImageStore.config.background?.solid?.color_code || "#FFFFFF";
	const [activeConfig, setActiveConfig] = useState<string>(imageToImageStore.activeConfig || "");
	const [selectedConfig, setSelectedConfig] = useState<string>("");
	const [operationState, setOperationState] = useState<OperationState>({
		isLoading: false,
		error: null,
		lastOperation: null,
	});

	const isSingleMode = playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE;

	const handleBackgroundConfigChange = (field: string, value: ConfigValue) => {
		imageToImageStore.handleConfigChange("background", {
			...imageToImageStore.config.background,
			[field]: value,
		});
		updateSelectedBackgroundConfigs();
	};

	useEffect(() => {
		isSingleMode && imageToImageStore.toggleBackgroundConfig(true);
		handleBackgroundConfigChange("generateByText", {
			...GENERATE_BY_TEXT_CONFIGS,
			num_results: imageToImageStore.config.background?.generateByText?.num_results,
		});

		return () => {
			imageToImageStore.toggleBackgroundConfig(false);
			if (isSingleMode) {
				imageToImageStore.handleConfigChange("background", DEFAULT_BACKGROUND_CONFIGS);
				imageToImageStore.handleNumResultsChange(1);
			}
		};
	}, []);

	useEffect(() => {
		setActiveConfig(t(`${imageToImageStore.activeConfig}`));
	}, [imageToImageStore.activeConfig]);

	const getErrorMessage = useCallback(
		(error: any, operation: OperationType): string => {
			const message = error?.message || error?.toString?.() || "";

			if (message.toLowerCase().includes("canceled")) {
				return t(`operations.${operation}.canceled`);
			}

			if (message.toLowerCase().includes("network")) {
				return t("errors.networkError");
			}

			if (message.toLowerCase().includes("timeout")) {
				return t("errors.timeoutError");
			}

			return t(`errors.${operation}Error`);
		},
		[t],
	);

	const handleOperation = useCallback(
		async (
			operation: OperationType,
			configUpdates: Record<string, any>,
			logEvent: () => void,
			validationFn?: () => boolean,
		) => {
			if (validationFn && !validationFn()) {
				setOperationState((prev) => ({
					...prev,
					error: t("errors.validationFailed"),
				}));
				return;
			}

			setOperationState((prev) => ({
				...prev,
				error: null,
				isLoading: true,
				lastOperation: operation,
			}));

			setSelectedConfig(t(operation));

			try {
				Object.entries(configUpdates).forEach(([key, value]) => {
					handleBackgroundConfigChange(key, value);
				});

				const num_results =
					operation === "generateByText" || operation === "generateByImage"
						? imageToImageStore.config.background?.[operation]?.num_results
						: 1;

				const configs: ImageToImageConfigType = {
					original_image: imageToImageStore.config.original_image,
					num_results: num_results ?? 1,
					background: {
						[operation]: imageToImageStore.config.background?.[operation],
					},
					content_moderation: imageToImageStore.config.content_moderation,
				};

				await imageToImageStore.generateImageToImage(undefined, configs, undefined, num_results);

				checkResolutions();
				logEvent();

				// Why to revert back the changes made at the time of the request?
				Object.entries(configUpdates).forEach(([key, value]) => {
					if (typeof value === "object" && value.selected !== undefined) {
						handleBackgroundConfigChange(key, { ...value, selected: false });
					} else if (typeof value === "boolean") {
						handleBackgroundConfigChange(key, false);
					}
				});

				setOperationState((prev) => ({
					...prev,
					isLoading: false,
					lastOperation: null,
				}));
			} catch (error: any) {
				const errorMessage = getErrorMessage(error, operation);

				setOperationState((prev) => ({
					...prev,
					isLoading: false,
					error: errorMessage,
					lastOperation: null,
				}));

				if (!errorMessage.toLowerCase().includes("cancel")) {
					console.error(`Error during ${operation}:`, error);
					Sentry.captureMessage(`Error while ${operation}: ${error}`);
				}
			}
		},
		[t, getErrorMessage, checkResolutions, imageToImageStore],
	);

	// Enhanced stop handler
	const handleStopOperation = useCallback(() => {
		imageToImageStore.abortImageGeneration();
		setOperationState((prev) => ({
			...prev,
			isLoading: false,
			error: null,
			lastOperation: null,
		}));
		setSelectedConfig("");
	}, [imageToImageStore]);

	const handleUploadRefImage = async (e: ChangeEvent<HTMLInputElement> | SyntheticEventProps) => {
		const file = e.target.files?.[0];
		e.target.value = "";

		if (file) {
			try {
				const ref_image_url = (await getBase64(file)) as string;
				handleBackgroundConfigChange("generateByImage", {
					...imageToImageStore.config.background?.generateByImage,
					selected: ref_image_url && ref_image_url.length > 0,
					ref_image_url,
				});
			} catch (error) {
				setOperationState((prev) => ({
					...prev,
					error: t("errors.imageUploadFailed"),
				}));
			}
		}
	};

	const handleDeleteRefImage = async () => {
		handleBackgroundConfigChange("generateByImage", {
			...imageToImageStore.config.background?.generateByImage,
			ref_image_url: "",
		});
	};

	const handleSolidColorChange = (color: string) => {
		imageToImageStore.handleConfigChange("background", {
			...imageToImageStore.config.background,
			solid: {
				selected: true,
				color_code: color,
			},
		});
		updateSelectedBackgroundConfigs();
	};

	// Enhanced button condition checker
	const getButtonCondition = useCallback(
		(operation: OperationType) => {
			return (
				imageToImageStore.isGeneratingImages &&
				selectedConfig === t(operation) &&
				operationState.lastOperation === operation
			);
		},
		[imageToImageStore.isGeneratingImages, selectedConfig, operationState.lastOperation, t],
	);

	// Validation functions
	const validateGenerateByText = () => {
		return countSelected > 0 && !!imageToImageStore.config.background?.generateByText?.prompt?.trim();
	};

	const validateGenerateByImage = () => {
		return countSelected > 0 && !!imageToImageStore.config.background?.generateByImage?.ref_image_url;
	};

	const validateBasicOperation = () => {
		return countSelected > 0;
	};

	return (
		<Box className={styles.backgroundConfigContainer}>
			{/* Generate by text */}
			<Config
				title={t("generateByText")}
				checked={imageToImageStore.config.background?.generateByText?.selected}
				onClick={(e) => {
					const target = e.target as HTMLInputElement;
					handleBackgroundConfigChange("generateByText", {
						...imageToImageStore.config.background?.generateByText,
						selected: target.checked,
						prompt: !target.checked ? "" : imageToImageStore.config.background?.generateByText?.prompt,
						mode: imageToImageStore.config.background?.generateByText?.mode ?? "high_control",
					});
					target.checked && setActiveConfig(t("generateByText"));
				}}
				isActive={activeConfig === t("generateByText") || canGenerateBackgroundByText()}
				setIsActive={setActiveConfig}
				titleOnly={isSingleMode}
				apiDocLink={
					isAutomotiveApp()
						? LinkConstants.PRODUCT_SHOT.AUTOMOTIVE.VEHICLE_BY_TEXT.DOCS
						: LinkConstants.BRIA_API_BACKGROUND_GENERATION
				}
			>
				<Box className={styles.accordionContainer}>
					<GenerateBackgroundModesCard />
					<BriaInput
						value={imageToImageStore.config.background?.generateByText?.prompt}
						placeholder={t("describeTheBackground")}
						onChange={(e) => {
							const target = e.target as HTMLInputElement;
							handleBackgroundConfigChange("generateByText", {
								...imageToImageStore.config.background?.generateByText,
								prompt: target.value,
								selected: target.value.trim().length > 0,
								...(isAutomotiveApp() && { binarize_alpha: true, refine_prompt: true }),
							});
						}}
						multiline
						minRows={3}
						className={styles.modelDescription}
					/>

					<InputLayout label={t("numberOfImages.label")} labelClassName={styles.inputLabel}>
						<BriaSlider
							value={imageToImageStore.config.background?.generateByText?.num_results}
							className={styles.numberSlider}
							onChange={(_e, value) => {
								handleBackgroundConfigChange("generateByText", {
									...imageToImageStore.config.background?.generateByText,
									num_results: value as number,
								});
							}}
							min={1}
							step={1}
							max={4}
							marks={Array.from({ length: 4 }, (_, index) => ({
								value: index + 1,
								label: `${index + 1}`,
							}))}
						/>
					</InputLayout>

					{isSingleMode && (
						<Box className={styles.configActions}>
							<StoppableButton
								condition={getButtonCondition("generateByText")}
								onStopButtonClick={handleStopOperation}
								onButtonClick={() =>
									handleOperation(
										"generateByText",
										{
											generateByText: {
												...imageToImageStore.config.background?.generateByText,
												selected: true,
											},
										},
										isAutomotiveApp() ? logAutomotiveGenerateByTextEvent : logGenerateByTextEvent,
										validateGenerateByText,
									)
								}
								disabled={!validateGenerateByText() || operationState.isLoading}
								buttonLabel={t("generate")}
								stopButtonLabel={t("stop")}
								size="small"
								fullWidth
								className={styles.generateButton}
							/>
						</Box>
					)}
				</Box>
			</Config>

			{/* Generate by image */}
			<Config
				title={t("generateByImage")}
				checked={imageToImageStore.config.background?.generateByImage?.selected}
				onClick={(e) => {
					const target = e.target as HTMLInputElement;
					handleBackgroundConfigChange("generateByImage", {
						...imageToImageStore.config.background?.generateByImage,
						selected: target.checked,
						ref_image_url: !target.checked
							? ""
							: imageToImageStore.config.background?.generateByImage?.ref_image_url,
					});
					target.checked && setActiveConfig(t("generateByImage"));
				}}
				isActive={activeConfig === t("generateByImage") || canGenerateBackgroundByImage()}
				setIsActive={setActiveConfig}
				titleOnly={isSingleMode}
				apiDocLink={
					isAutomotiveApp()
						? LinkConstants.BRIA_API_LIFESTYLE_PRODUCT_SHOT_BY_IMAGE
						: LinkConstants.BRIA_API_BACKGROUND_GENERATION
				}
			>
				<Box className={styles.accordionContainer}>
					<ImageUploader
						description={t("uploadImage")}
						supportedFilesText={
							"Match the background from your image (PNG, JPEG, JPG, WebP), Limited up to 12MB."
						}
						icon={<CloudUploadSVG />}
						horizontalLayout
						largeIcon
						maxFilesLimit={1}
						maxFileSize={12 * 1024 * 1024}
						src={imageToImageStore.config.background?.generateByImage?.ref_image_url}
						onUpload={(e) => handleUploadRefImage(e)}
						onDelete={() => handleDeleteRefImage()}
					/>
					<InputLayout label={t("numberOfImages.label")} labelClassName={styles.inputLabel}>
						<BriaSlider
							value={imageToImageStore.config.background?.generateByImage?.num_results}
							className={styles.numberSlider}
							onChange={(_e, value) => {
								handleBackgroundConfigChange("generateByImage", {
									...imageToImageStore.config.background?.generateByImage,
									num_results: value as number,
								});
							}}
							min={1}
							step={1}
							max={4}
							marks={Array.from({ length: 4 }, (_, index) => ({
								value: index + 1,
								label: `${index + 1}`,
							}))}
						/>
					</InputLayout>
					{isSingleMode && (
						<Box className={styles.configActions}>
							<StoppableButton
								condition={getButtonCondition("generateByImage")}
								onStopButtonClick={handleStopOperation}
								onButtonClick={() =>
									handleOperation(
										"generateByImage",
										{
											generateByImage: {
												...imageToImageStore.config.background?.generateByImage,
												selected: true,
											},
										},
										isAutomotiveApp() ? logAutomotiveGenerateByImageEvent : logGenerateByImageEvent,
										validateGenerateByImage,
									)
								}
								disabled={!validateGenerateByImage() || operationState.isLoading}
								buttonLabel={t("generate")}
								stopButtonLabel={t("stop")}
								size="small"
								fullWidth
								className={styles.generateButton}
							/>
						</Box>
					)}
				</Box>
			</Config>

			{/* Solid */}
			<Config
				title={t("solid")}
				checked={imageToImageStore.config.background?.solid?.selected}
				onClick={(e) => {
					const target = e.target as HTMLInputElement;
					handleBackgroundConfigChange("solid", {
						selected: target.checked,
						color_code: solidColor,
					});
					target.checked && setActiveConfig(t("solid"));
				}}
				isActive={activeConfig === t("solid") || canApplySolidBackground()}
				setIsActive={setActiveConfig}
				titleOnly={isSingleMode}
				apiDocLink={LinkConstants.BRIA_API_BACKGROUND_GENERATION}
			>
				<Box className={styles.solidColorContainer}>
					<BriaColorPicker
						color={solidColor}
						onChange={handleSolidColorChange}
						showInputField
						onColorPickerOpen={onSubMenuOpen}
					/>
					{isSingleMode && (
						<Box className={styles.configActions}>
							<StoppableButton
								condition={getButtonCondition("solid")}
								onStopButtonClick={handleStopOperation}
								onButtonClick={() =>
									handleOperation(
										"solid",
										{
											solid: {
												selected: true,
												color_code: solidColor,
											},
										},
										isAutomotiveApp() ? logAutomotiveSolidBackgroundEvent : logSolidBackgroundEvent,
										validateBasicOperation,
									)
								}
								disabled={!validateBasicOperation() || operationState.isLoading}
								buttonLabel={t("generate")}
								stopButtonLabel={t("stop")}
								size="small"
								fullWidth
								className={styles.generateButton}
							/>
						</Box>
					)}
				</Box>
			</Config>

			{/* Remove */}
			<Config
				title={isAutomotiveApp() ? t("removeBackground") : t("remove")}
				checked={imageToImageStore.config.background?.remove}
				onClick={(e) => {
					const target = e.target as HTMLInputElement;
					handleBackgroundConfigChange("remove", target.checked);
				}}
				titleOnly={isSingleMode}
				isActive
				className={styles.config}
				apiDocLink={LinkConstants.BRIA_API_BACKGROUND_REMOVE}
				apiLinkPosition={isAutomotiveApp() && isSingleMode ? "bottom" : "right"}
			>
				{isSingleMode && (
					<StoppableButton
						condition={getButtonCondition("remove")}
						onStopButtonClick={handleStopOperation}
						onButtonClick={() =>
							handleOperation(
								"remove",
								{ remove: true },
								isAutomotiveApp() ? logAutomotiveRemoveBackgroundEvent : logRemoveBackgroundEvent,
								validateBasicOperation,
							)
						}
						disabled={!validateBasicOperation() || operationState.isLoading}
						buttonLabel={t("remove")}
						stopButtonLabel={t("stop")}
						size="small"
						fullWidth
						className={styles.removeButton}
					/>
				)}
			</Config>

			{/* Expand / Original background */}
			{!isSingleMode && (
				<Config
					title={t("expand")}
					checked={imageToImageStore.config.background?.expand}
					onClick={(e) => {
						const target = e.target as HTMLInputElement;
						handleBackgroundConfigChange("expand", target.checked);
					}}
				/>
			)}

			{/* blur */}
			{!hiddenConfigs?.includes("blur") && (
				<Config
					title={t("blur")}
					checked={imageToImageStore.config.background?.blur}
					onClick={(e) => {
						const target = e.target as HTMLInputElement;
						handleBackgroundConfigChange("blur", target.checked);
					}}
					titleOnly={isSingleMode}
					isActive
					className={styles.config}
					apiDocLink={LinkConstants.BRIA_API_BACKGROUND_BLUR}
				>
					{isSingleMode && (
						<StoppableButton
							condition={getButtonCondition("blur")}
							onStopButtonClick={handleStopOperation}
							onButtonClick={() =>
								handleOperation("blur", { blur: true }, logBlurBackgroundEvent, validateBasicOperation)
							}
							disabled={!validateBasicOperation() || operationState.isLoading}
							buttonLabel={t("blur")}
							stopButtonLabel={t("stop")}
							size="small"
							fullWidth
							className={styles.blurButton}
						/>
					)}
				</Config>
			)}
		</Box>
	);
};

export default observer(BackgroundConfig);
